import React, {useState, useEffect, useCallback, useRef} from 'react';
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { iconHeight, iconLength, iconLink, iconThickness } from "../../../../../assetsSQFT";
import sqftConfig from "../../../../canvas/store/sqftConfig";
import { is3DMode } from "../../../NavigationHelper";
import { isShapeTallCabinet, MINIMUM_TALL_CABINET_HEIGHT } from "../../../../../helpers/sqftCabinetsDataMapper";
import { getCabinetInteriorHeight } from "../../../../../helpers/tallCabinetInteriorHelper";
import { updateTallCabinetIn3D, updateCabinetPositionIn3D } from "../../../../ar3d/threeD/glbViewer/scene/modelsLoader/cabinetInterior/tallCabinetUpdate3D";
import { reloadCountertopsIn3DMode } from "../../../../../helpers/3dHelper/modelsHelper";
import { normalizeLayoutForShape } from "../helpers/cabinetInteriorLayoutHelper";
import { SHAPE_CORNER_LAYOUTS } from "../../../../canvas/core/cabinet/cabinetShapes";
import {
  getCornerCabinetDepth,
  getCornerCabinetMaxDepth,
  getCornerCabinetMinDepth,
  getCornerCabinetMinWidth
} from "../../../../../helpers/shapeHelper";

const MIN_LOGICAL_VALUE = 6;
const MAX_VALUE = sqftConfig.measureUnit === "Metric" ? 15000 : 700;

const CustomSizeList = (props) => {
  const {isEnabled, shape, listKey, refreshCabinetAttributes} = props;
  const isTallCabinet = isShapeTallCabinet(shape);
  const shapeLayout = shape && shape.shapeParams && (shape.shapeParams.shapeLayout || shape.shapeParams.layout);
  const isCornerCabinet = shapeLayout && SHAPE_CORNER_LAYOUTS.includes(shapeLayout);

  const getSafeValue = useCallback((getter) => {
    if (!shape || typeof shape[getter] !== 'function') {
      return MIN_LOGICAL_VALUE.toString();
    }
    const value = shape[getter]();
    return (value !== undefined && value !== null) ? value.toString() : MIN_LOGICAL_VALUE.toString();
  }, [shape]);

  const getSafeDepthValue = useCallback(() => {
    return isCornerCabinet ? getCornerCabinetDepth(shape).toString() : getSafeValue('getDepth');
  }, [shape, isCornerCabinet, getSafeValue]);

  const [heightInput, setHeightInput] = useState(() => getSafeValue('getHeight'));
  const [widthInput,  setWidthInput]  = useState(() => getSafeValue('getWidth'));
  const [depthInput,  setDepthInput]  = useState(() => getSafeDepthValue());
  const countertopsReloadTimeout = useRef(null);

  const minHeightValue = isTallCabinet ? MINIMUM_TALL_CABINET_HEIGHT : MIN_LOGICAL_VALUE;
  const minWidthValue = isCornerCabinet ? getCornerCabinetMinWidth(shape, MIN_LOGICAL_VALUE) : MIN_LOGICAL_VALUE;
  const minDepthValue = isCornerCabinet ? getCornerCabinetMinDepth(shape, MIN_LOGICAL_VALUE) : MIN_LOGICAL_VALUE;
  const maxDepthValue = isCornerCabinet ? Math.max(minDepthValue, getCornerCabinetMaxDepth(shape, MAX_VALUE)) : MAX_VALUE;

  useEffect(() => {
    setHeightInput(getSafeValue('getHeight'));
    setWidthInput(getSafeValue('getWidth'));
    setDepthInput(getSafeDepthValue());
  }, [shape, listKey, getSafeValue, getSafeDepthValue]);

  useEffect(() => () => {
    if (countertopsReloadTimeout.current) {
      clearTimeout(countertopsReloadTimeout.current);
    }
  }, []);

  const fixValue = (valStr, minValue = MIN_LOGICAL_VALUE) => {
    const val = parseFloat(valStr);
    if (isNaN(val)) return minValue;
    if (val < minValue) return minValue;
    if (val > MAX_VALUE) return MAX_VALUE;
    return val;
  };

  const reloadCountertopsForShapes = (shapes) => {
    reloadCountertopsIn3DMode(shapes).catch(e =>
      console.error('[3D resize] countertops reload error', e)
    );
  };

  const scheduleCountertopsReload = (shapes, isFinalUpdate) => {
    if (countertopsReloadTimeout.current) {
      clearTimeout(countertopsReloadTimeout.current);
    }

    if (isFinalUpdate) {
      reloadCountertopsForShapes(shapes);
      return;
    }

    countertopsReloadTimeout.current = setTimeout(() => {
      reloadCountertopsForShapes(shapes);
    }, 300);
  };

  const updateTallCabinetGeometry = (isFinalUpdate = false, shiftedShapes = []) => {
    if (!is3DMode() || !isTallCabinet || !shape.shapeCabinet) {
      return;
    }

    refreshCabinetAttributes();
    updateTallCabinetIn3D(shape.id, shape.shapeCabinet.getCabinetInteriorLayout());

    const shiftedShapesToSync = shiftedShapes.filter(shiftedShape => shiftedShape.id !== shape.id);
    shiftedShapesToSync.forEach(shiftedShape => {
      updateCabinetPositionIn3D(shiftedShape.id);
    });

    scheduleCountertopsReload([shape, ...shiftedShapesToSync], isFinalUpdate);
  };

  const refreshDimensionChange = (isFinalUpdate = false, shiftedShapes = []) => {
    if (is3DMode() && isTallCabinet && shape.shapeCabinet) {
      updateTallCabinetGeometry(isFinalUpdate, shiftedShapes);
      return;
    }

    refreshCabinetAttributes();
  };

  const syncInputsAfterDimensionChange = () => {
    if (isCornerCabinet) {
      setWidthInput(getSafeValue('getWidth'));
      setDepthInput(getCornerCabinetDepth(shape).toString());
    }
  };

  const applyDimensionChange = (dimension, value, shouldFixValue) => {
    const setterName = dimension === 'width' ? 'setWidth' : 'setDepth';
    const setInput = dimension === 'width' ? setWidthInput : setDepthInput;
    const minValue = dimension === 'width' ? minWidthValue : minDepthValue;
    const maxValue = dimension === 'width' ? MAX_VALUE : maxDepthValue;

    setInput(value);

    if (!shape || typeof shape[setterName] !== 'function') {
      return;
    }

    if (shouldFixValue) {
      const fixed = fixValue(value, minValue);
      const safeFixed = Math.min(fixed, maxValue);
      const shiftedShapes = shape[setterName](safeFixed) || [];
      setInput(safeFixed.toString());
      syncInputsAfterDimensionChange();
      refreshDimensionChange(true, shiftedShapes);
      return;
    }

    const nextValue = parseFloat(value);
    if (isNaN(nextValue) || nextValue < minValue || nextValue > maxValue) {
      return;
    }

    const shiftedShapes = shape[setterName](nextValue) || [];
    syncInputsAfterDimensionChange();
    refreshDimensionChange(false, shiftedShapes);
  };

  const onBlurWidth = () => {
    applyDimensionChange('width', widthInput, true);
  };

  const onBlurDepth = () => {
    applyDimensionChange('depth', depthInput, true);
  };

  const updateTallCabinetHeight = (shouldNormalizeSections) => {
    if (!is3DMode() || !isTallCabinet || !shape.shapeCabinet) {
      return;
    }

    let cabinetInteriorLayout = shape.shapeCabinet.getCabinetInteriorLayout();

    if (shouldNormalizeSections) {
      cabinetInteriorLayout = normalizeLayoutForShape(
        cabinetInteriorLayout,
        getCabinetInteriorHeight(shape.getHeight())
      );

      if (cabinetInteriorLayout) {
        shape.shapeCabinet.setCabinetInteriorLayout(cabinetInteriorLayout);
      }
    }

    refreshCabinetAttributes();
    updateTallCabinetIn3D(shape.id, cabinetInteriorLayout);
  };

  const applyHeightChange = (value, shouldNormalizeSections) => {
    setHeightInput(value);

    if (!shape) {
      return;
    }

    if (shouldNormalizeSections) {
      const fixed = fixValue(value, minHeightValue);
      shape.setHeight(fixed);
      setHeightInput(fixed.toString());
      updateTallCabinetHeight(true);
      return;
    }

    if (!is3DMode() || !isTallCabinet) {
      return;
    }

    const nextHeight = parseFloat(value);
    if (isNaN(nextHeight) || nextHeight < minHeightValue || nextHeight > MAX_VALUE) {
      return;
    }

    shape.setHeight(nextHeight);
    updateTallCabinetHeight(false);
  };

  if (!shape) {
    return null;
  }

  return (
    <div className={isEnabled ? "" : " segment-container--disabled"}>
      <ul className="mitered-segment segment-params">
        {/* HEIGHT */}
        <li className="mitered-segment__content segment-height">
          <div className="segment-image">
            <img src={iconHeight} alt="Height"/>
          </div>
          <div className="segment-value">
            <input
              className="ui-input segment-value-input"
              type="number"
              min={minHeightValue}
              max={MAX_VALUE}
              value={heightInput}
              onFocus={e => e.target.select()}
              onChange={e => applyHeightChange(e.target.value, false)}
              onBlur={e => applyHeightChange(e.target.value, true)}
            />
          </div>
          <div className="segment-description">
            <div><img src={iconLink} alt=""/></div>
            <div><FormattedMessage id="sqft.navigation.mitered.height"/></div>
          </div>
        </li>

        {/* WIDTH */}
        <li className="mitered-segment__content segment-length">
          <div className="segment-image">
            <img src={iconLength} alt="Width"/>
          </div>
          <div className="segment-value">
            <input
              className="ui-input segment-value-input"
              type="number"
              min={minWidthValue}
              max={MAX_VALUE}
              value={widthInput}
              onFocus={e => e.target.select()}
              onChange={e => applyDimensionChange('width', e.target.value, false)}
              onBlur={onBlurWidth}
            />
          </div>
          <div className="segment-description">
            <FormattedMessage id="sqft.manual.column.width"/>
          </div>
        </li>

        {/* DEPTH */}
        <li className="mitered-segment__content segment-thickness">
          <div className="segment-image">
            <img src={iconThickness} alt="Depth"/>
          </div>
          <div className="segment-value">
            <input
              className="ui-input segment-value-input"
              type="number"
              min={minDepthValue}
              max={maxDepthValue}
              value={depthInput}
              onFocus={e => e.target.select()}
              onChange={e => applyDimensionChange('depth', e.target.value, false)}
              onBlur={onBlurDepth}
            />
          </div>
          <div className="segment-description">
            <FormattedMessage id="sqft.navigation.waterfall.depth"/>
          </div>
        </li>
      </ul>
    </div>
  );
};

CustomSizeList.propTypes = {
  isEnabled: PropTypes.bool,
  shape: PropTypes.object.isRequired,
  listKey: PropTypes.number,
  refreshCabinetAttributes: PropTypes.func.isRequired,
};

const mapStateToProps = (store) => {
  return {
    menuOpened: store.sqftMenuState.menuOpened,
  };
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(CustomSizeList);
